import moment from "moment";
import { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import MUIGrid from "@mui/material/Grid";
import MUIStack from "@mui/material/Stack";
import MUIButton from "@mui/material/Button";
import MUITypography from "@mui/material/Typography";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIDivider from "@mui/material/Divider";
import MUIChip from "@mui/material/Chip";
import MUISaveButton from "@mui/icons-material/Save";
import MUIContentCopyIcon from '@mui/icons-material/ContentCopy';
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIChildCareIcon from "@mui/icons-material/ChildCare";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import MUICloudSyncIcon from "@mui/icons-material/CloudSync";
import MUISwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MUILocalShippingIcon from "@mui/icons-material/LocalShipping";
import MUICachedIcon from '@mui/icons-material/Cached';
import MUIAttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MUIFeedbackIcon from "@mui/icons-material/Feedback";
import MUISaveIcon from "@mui/icons-material/Save";
import MUIHighlightOffIcon from '@mui/icons-material/HighlightOff';
import MUIUndoIcon from '@mui/icons-material/Undo';
import MUIAddIcon from '@mui/icons-material/Add';
import MUIArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import MUISpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogTitle from "@mui/material/DialogTitle";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIDialogActions from "@mui/material/DialogActions";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUIArrowBackIcon from "@mui/icons-material/ArrowBack";
import MUILinkIcon from "@mui/icons-material/Link";

import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import ChipOrderStatusReason from "../../Chip/ChipOrderStatusReason";
import Menu from "../../Menu";
import Format from "../../../Utility/Format";
import Patient from "../../../Utility/Crud/Patient";
import IssueCategory from "../../../Utility/Crud/IssueCategory";
import Location from "../../../Utility/Crud/Location";
import User from "../../../Utility/Crud/User";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import api from "../../../Seating/Security/api";
import InsuranceSubType from "../../../Utility/Crud/InsuranceSubType";
import Validator from "../../../Utility/Validator";
import Security from "../../../Utility/Security";
import DialogCancelReopenTicket from "../../Dialog/DialogCancelReopenTicket";
import DialogChangeParentOrder from "../../Dialog/DialogChangeParentOrder";
import Setting from "../../../Utility/Setting";

/**
 * ChartHeader component that adapts its layout based on context (patient vs
 * order level)
 * @param {Object} props
 * @param {Function} props.onSaveChart - Callback function for save button
 * @returns {JSX.Element} The ChartHeader component
 */
export default function CardChartHeader() {
    const [primaryProduct, setPrimaryProduct] = useState(null);
    const [parentOrder, setParentOrder] = useState(null);
    const [serviceOrderReason, setServiceOrderReason] = useState(null);
    const [shouldSaveChart, setShouldSaveChart] = useState(false);
    const [processingATPDocument, setProcessingATPDocument] = useState(false);

    const mainContext = useContext(MainContext);
    const chartContext = useContext(ChartContext);
    const [activity, setActivity] = useState(chartContext.originalChart.activity[chartContext.orderId]);
    const patient = chartContext.originalChart.patient[chartContext.patientId];
    const isPatientView = !window.location.pathname.includes('/order/');
    const orderActivity = chartContext.originalChart.orderActivity[chartContext.orderId];
    const [openDialogCancelReopenPopup, setOpenDialogCancelReopenPopup] = useState(false);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const [buttonText, setButtonText] = useState(null);
    const [headerText, setHeaderText] = useState(null);
    const [openChangeViewDialog, setOpenChangeViewDialog] = useState(false);
    const [openChangeParentOrderDialog, setOpenChangeParentOrderDialog] = useState(false);
    const [selectedChartView, setSelectedChartView] = useState(
        chartContext.chartViews[chartContext.selectedChartViewId]
    );

    useEffect(() => {
        if (!orderActivity) {
            return;
        }
        const activity = chartContext.chart.activity[orderActivity.id];
        setActivity(activity);
        setServiceOrderReason(OrderActivity.getServiceOrderReason(orderActivity, chartContext.chart));

        setPrimaryProduct(
            OrderActivity.getPrimaryProduct(
                OrderActivity.getProducts(
                    OrderActivity.getParentOrder(
                        orderActivity.id,
                        chartContext.chart
                    ),
                    chartContext.chart
                )
            )
        );

        setParentOrder(OrderActivity.getParentOrder(chartContext.orderId, chartContext.originalChart));
    }, [chartContext.orderId, chartContext.originalChart]);

    useEffect(() => {
        switch (chartContext.pageMode) {
            case "service":
            case "pickup":
                setButtonText("Create Ticket");
                setHeaderText(`Create ${Format.titleCase(chartContext.pageMode)} Ticket`);
                break;
            case "billingIssue":
                setButtonText("Create Ticket");
                setHeaderText(`Create Billing Issue Ticket`);
                break;
            case "resupply":
                setButtonText(`Create Re-Supply Order`);
                setHeaderText(`Create Re-Supply Order`);
                break;
            case "new":
                setButtonText("Create New Order");
                setHeaderText("Create New Order");
                break;
            case "atpAssessment":
                setButtonText("Save");
                setHeaderText(`Create ATP Assessment`);
                break;
            case "atpAssessmentEdit":
                setButtonText("Save");
                setHeaderText(`Edit ATP Assessment`);
                break;
            case "atpAssessmentSign":
                setButtonText("Sign & Upload");
                setHeaderText(`Sign ATP Assessment`);
                break;
            case "homeAssessment":
                setButtonText("Save");
                setHeaderText(`Create Home Assessment`);
                break;
            case "homeAssessmentEdit":
                setButtonText("Save");
                setHeaderText(`Edit Home Assessment`);
                break;
            default:
                setButtonText("Save");
                setHeaderText(`Create ${Format.titleCase(chartContext.pageMode)}`);
                break;
        }

    }, [chartContext.pageMode]);

    // Save the chart when the shouldSaveChart state is true
    useEffect(() => {
        if (shouldSaveChart) {
            chartContext.handleSaveChart(null, () => {
                history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}`);
            });
            setShouldSaveChart(false);
        }
    }, [shouldSaveChart]);

    /**
     * Renders the patient demographics information.
     * @returns {JSX.Element} Grid layout with patient demographics information
     * display
     */
    const renderPatientDemographics = () => {
        return (
            <MUIGrid item lg={"auto"} key="patient-demographics">
                <MUITypography
                    color="primary"
                    variant="h6"
                    component="span"
                    onClick={() => chartContext.orderId && chartContext.handleNavigateChart(`/chart/${chartContext.patientId}`)}
                    sx={{
                        display: "inline",
                        cursor: chartContext.orderId ? "pointer" : "default"
                    }}
                >
                    {Patient.getDisplayName(patient)}
                </MUITypography>
                <MUITypography variant="body2">{Patient.getDemographicSummary(patient, mainContext.termsIndexed)}</MUITypography>
            </MUIGrid>
        )
    };

    /**
     * Renders patient sales rep and location.
     * @returns {JSX.Element} Grid layout with patient demographics information
     * display
     */
    const renderSalesRepAndLocation = () => {
        const salesRepUserprofile = Patient.getSalesRepUserprofile(chartContext.chart, mainContext.usersIndexed);
        const location = Patient.getLocation(chartContext.chart, mainContext.locationsIndexed);

        return (
            <MUIGrid item lg={"auto"} key="sales-rep-location">
                <MUITypography variant="body2">
                    Sales Rep: {salesRepUserprofile ? User.getDisplayName(salesRepUserprofile) : "N/A"}
                </MUITypography>
                <MUITypography variant="body2">
                    Location: {location ? Location.getDisplayName(location) : "N/A"}
                </MUITypography>
            </MUIGrid>
        );
    }

    /**
     * Renders the brightree patient information.
     * @returns {JSX.Element} Grid layout with brightree patient information
     * display
     */
    const renderPatientBrightreeExternalId = () => {
        if (!(patient.brightreeExternalId && patient.brightreeExternalKey)) {
            return null;
        }
        return (
            <MUIGrid item lg={"auto"} key="patient-bt-external-id">
                <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                    <MUITypography variant="body2">{`BT Patient: ${patient.brightreeExternalId}`}</MUITypography>
                    <MUIOpenInNewIcon
                        fontSize="small"
                        sx={{ color: (theme) => theme.palette.secondary.main, cursor: 'pointer' }}
                        onClick={() => window.open(Patient.getBrightreeUrl(patient), '_blank')}
                    />
                </MUIStack>
            </MUIGrid>
        );
    }

    /**
     * Renders the patient indicators.
     * @returns {JSX.Element} Grid layout with patient indicators display
     */
    const renderIndicators = () => {
        const indicators = [];

        let issueReasonCountMap = {};
        Object.values(chartContext.originalChart.patientIssue).forEach((patientIssue) => {
            let issueReason = mainContext.issueReasonsIndexed[patientIssue.issueReasonId];
            const issueCategoryName = IssueCategory.getDisplayName(mainContext.issueCategoriesIndexed[issueReason?.issueCategoryId] ?? null);

            if (issueReason && issueReason.showAlert && patientIssue.issueStatus !== 1 && issueCategoryName !== null) {
                issueReasonCountMap[issueCategoryName] = (issueReasonCountMap[issueCategoryName] || 0) + 1;
            }
        });

        const patientBalance = patient.collectionsBalance;
        let USDollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });

        if (patient.dateOfBirth) {
            const age = moment([]).diff(new Date(patient.dateOfBirth), 'years');
            if (age < 18) {
                indicators.push(
                    <MUIChip
                        key="pediatric"
                        label="Pediatric"
                        size="small"
                        color="primary"
                        icon={<MUIChildCareIcon />}
                    />
                );
            }
        }

        // Add Priority indicator if activity.priority is true
        if (activity && activity.priority) {
            indicators.push(
                <MUIChip
                    key="priority"
                    label="Priority"
                    size="small"
                    color="error"
                    icon={<MUIArrowCircleUpIcon />}
                />
            );
        }

        if (patientBalance >= 0 && patientBalance) {
            indicators.push(
                <MUIChip
                    key="collections"
                    label={`Collections: ${USDollar.format(patientBalance)}`}
                    size="small"
                    color="error"
                    icon={<MUIAttachMoneyIcon />}
                />
            );
        }

        if (issueReasonCountMap) {
            Object.keys(issueReasonCountMap).length > 0 && Object.keys(issueReasonCountMap).map((key, index) => {
                const value = issueReasonCountMap[key] > 1 ? ` ${key} (${issueReasonCountMap[key]})` : `${key}`;
                indicators.push(
                    <MUIChip
                        key={`issue-${key}`}
                        label={value}
                        size="small"
                        color="error"
                        icon={<MUIFeedbackIcon />}
                    />
                );
            });
        }

        if (orderActivity && orderActivity.needsRedelivery) {
            indicators.push(
                <MUIChip
                    key="needs-redelivery"
                    label="Needs Redelivery"
                    size="small"
                    color="info"
                    icon={<MUILocalShippingIcon />}
                />
            );
        }

        if (indicators.length === 0) {
            return null;
        }

        return (
            <MUIGrid item lg={"auto"} key="patient-indicators">
                <MUIStack direction="row" spacing={1}>
                    {indicators}
                </MUIStack>
            </MUIGrid>
        );
    };

    /**
     * Renders the order header.
     * @returns {JSX.Element} Grid layout with order type, id, and order status
     * reason
     */
    const renderOrderNumberAndServiceOrderReason = () => {
        return (
            <MUIGrid item lg={"auto"} key="order-header">
                <MUIStack direction="row" spacing={1} alignItems="center">
                    <MUITypography variant="body2">{`${orderActivity.orderType} ${activity?.type} #${orderActivity.id}`}</MUITypography>
                    <MUIContentCopyIcon
                        sx={{ cursor: 'pointer', fontSize: '1rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(orderActivity.id);
                            NotistackEnqueueSnackbar("Order # copied to clipboard", { variant: "success" });
                        }}
                    />
                </MUIStack>
                {serviceOrderReason && (
                    <MUITypography variant="body2" sx={{ fontStyle: 'italic' }}>{serviceOrderReason.serviceReason}</MUITypography>
                )}
            </MUIGrid>
        );
    };

    /**
     * Renders the order equipment.
     * @returns {JSX.Element} Grid layout with order equipment display
     */
    const renderOrderEquipmentAndSerial = () => {
        if (!primaryProduct) {
            return null;
        }

        return (
            <MUIGrid item lg={"auto"} key="order-equipment">
                <MUITypography variant="body2">{OrderActivity.getProductDisplayName(primaryProduct, mainContext.baseUnitsIndexed, mainContext.vendorsIndexed, mainContext.productSubTypesIndexed) || null}</MUITypography>
                <MUITypography variant="body2">{primaryProduct?.serial || null}</MUITypography>
            </MUIGrid>
        )
    }

    /**
     * Renders the order insurance.
     * @returns {JSX.Element} Grid layout with order insurance display
     */
    const renderOrderInsurance = () => {
        const primaryInsuranceSubType = mainContext.insuranceSubTypesIndexed[orderActivity.insuranceSubTypeId];
        const secondaryInsuranceSubType = mainContext.insuranceSubTypesIndexed[orderActivity.secondaryInsuranceSubTypeId];

        if (!primaryInsuranceSubType && !secondaryInsuranceSubType) {
            return null;
        }

        return (
            <MUIGrid item lg={"auto"} key="order-insurance">
                {primaryInsuranceSubType && (
                    <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        <MUITypography variant="body2">
                            {InsuranceSubType.getDisplayName(primaryInsuranceSubType)}
                        </MUITypography>
                    </MUIStack>
                )}
                {secondaryInsuranceSubType && (
                    <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                        <MUITypography variant="body2">
                            {InsuranceSubType.getDisplayName(secondaryInsuranceSubType)}
                        </MUITypography>
                    </MUIStack>
                )}
            </MUIGrid>
        );
    }

    /**
     * Renders the brightree order information.
     * @returns {JSX.Element} Grid layout with brightree order information
     * display
     */
    const renderOrderBrightreeExternalId = () => {
        // Only show patient Brightree external ID if both brightreeExternalId and brightreeExternalKey are present
        const patientSection = (patient.brightreeExternalId && patient.brightreeExternalKey) ? (
            <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <MUITypography variant="body2">{`BT Patient: ${patient.brightreeExternalId}`}</MUITypography>
                <MUIOpenInNewIcon
                    fontSize="small"
                    sx={{ color: (theme) => theme.palette.secondary.main, cursor: 'pointer' }}
                    onClick={() => window.open(Patient.getBrightreeUrl(patient), '_blank')}
                />
            </MUIStack>
        ) : null;

        // Show order Brightree external ID if present, using brightreeExternalId with fallback to btSoId
        const orderBrightreeId = orderActivity.brightreeExternalId || orderActivity.btSoId;
        const orderSection = orderBrightreeId ? (
            <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <MUITypography variant="body2">{`BT Order: ${orderBrightreeId}`}</MUITypography>
                <MUIOpenInNewIcon
                    fontSize="small"
                    sx={{ color: (theme) => theme.palette.secondary.main, cursor: 'pointer' }}
                    onClick={() => window.open(OrderActivity.getBrightreeUrl(orderActivity), '_blank')}
                />
            </MUIStack>
        ) : null;

        if (!patientSection && !orderSection) {
            return null;
        }

        return (
            <MUIGrid item lg={"auto"} key="order-bt-external-id">
                {patientSection}
                {orderSection}
            </MUIGrid>
        );
    }

    const reloadChartData = async () => {

        await chartContext.reloadChartData();
    }
    /**
     * Renders the order status reason.
     * @returns {JSX.Element} Grid layout with created at date and service
     * reason display
     */
    const renderOrderStatusReason = () => {
        if (!activity) {
            return null;
        }

        return (
            <MUIGrid item lg={"auto"} key="order-status-reason">
                <ChipOrderStatusReason
                    orderStatusReason={chartContext.chart?.orderStatusReason?.[orderActivity.orderStatusReasonId]}
                    orderStatusReasonDetail={chartContext.chart?.orderStatusReasonDetail?.[orderActivity.orderStatusReasonDetailId]}
                />
            </MUIGrid>
        );
    }

    // Add Brightree create/update functions
    const createBrightreePatient = (patientId) => {
        api.send("GET", "/syncPatientToBt/" + patientId)
            .then(() => {
                NotistackEnqueueSnackbar('Patient has been created in Brightree.', { variant: "success" });
            })
            .catch((error) => {
                console.error(error)
                NotistackEnqueueSnackbar('There was an error creating Brightree Patient', { variant: "error" });
            });
        return;
    };

    const updateBrightreePatient = (patientId) => {
        api.send("GET", "/syncPatientToBt/" + patientId)
            .then(() => {
                NotistackEnqueueSnackbar('Patient has been updated in Brightree.', { variant: "success" });
            })
            .catch((error) => {
                console.error(error)
                NotistackEnqueueSnackbar('There was an error updating Brightree Patient', { variant: "error" });
            });
        return;
    };

    const createBrightreeOrder = (orderId) => {
        return api.send("POST", "/orders/syncToBt/" + orderId)
            .then(() => {
                NotistackEnqueueSnackbar('Order has been created in Brightree.', { variant: "success" });
            })
            .catch((error) => {
                console.error(error);
                NotistackEnqueueSnackbar('There was an error creating Brightree Order', { variant: "error" });
            });
    };

    const handleApplyChangeView = () => {
        if (selectedChartView) {
            Setting.set("chart.selectedChartViewId", selectedChartView.id);
            chartContext.setSelectedChartViewId(selectedChartView.id);
        }
        setOpenChangeViewDialog(false);
    };

    const renderNewPageModeButtons = () => {
        const handleClickCreateTicket = () => {
            // Validate the rest of the chart fields before allowing ticket creation
            const chartValidationIssues = chartContext.validateChartFields(chartContext.validationRules);
            const validationIssues = Validator.validate(
                chartContext.createTicketValidationRules,
                {
                    confirm: { type: "confirm" },
                    routing: { required: true },
                }
            );
            chartContext.setCreateTicketValidationIssues(validationIssues);

            if (Object.keys(validationIssues).length === 0 && Object.keys(chartValidationIssues).length === 0) {
                // Signal Routing to perform save + post actions
                chartContext.setPendingCreateTicket(true);
            } else {
                NotistackEnqueueSnackbar("Please correct the displayed issues.", { variant: "error" });
            }
        };

        const handleCreateAtpAssessmentSignature = () => {
            const sanitizeFilename = (filename) => {
                return filename
                    .toLowerCase()
                    .replaceAll(/[^.a-z0-9_-]/gi, '_');
            }

            const sanitizedFilename = sanitizeFilename(chartContext.uploadedFile.name);
            const formData = new FormData();

            formData.append("activityId", parseInt(chartContext.orderId));
            formData.append("filename", sanitizedFilename);
            formData.append("userId", parseInt(mainContext.currentUser.id));
            formData.append("file", chartContext.uploadedFile);

            const rules = { "CardChartSignature": chartContext.validationRules["CardChartSignature"] };
            const validationIssues = chartContext.validateChartFields(rules);

            const assistiveTechnologyReportActivityFileTypeId = Object.values(mainContext.termsIndexed || {}).find(term => term.type === 'activity_file_type' && term.name === 'Assistive Technology Report')?.id || null;
            formData.append("activityFileTypeTermId", assistiveTechnologyReportActivityFileTypeId);

            const atpAssessment = Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
                return atpAssessment.orderActivityId === chartContext.orderId;
            }) || [];
            setProcessingATPDocument(true);
            if (Object.keys(validationIssues).length === 0) {
                api.send("POST", "/activities/uploadFile", formData)
                    .then((res) => {
                        chartContext.onChange({
                            atpAssessment: {
                                [atpAssessment.id]: {
                                    activityFileId: res.activityFileId
                                }
                            }
                        });
                        setShouldSaveChart(true);
                        setProcessingATPDocument(false);
                    })
                    .catch((error) => {
                        console.error("Error uploading file", error);
                        NotistackEnqueueSnackbar("Error uploading file", { variant: "error" });
                        setProcessingATPDocument(false);
                    });
            } else {
                NotistackEnqueueSnackbar("Please correct the displayed issues.", { variant: "error" });
                setProcessingATPDocument(false);
            }
        };

        if (chartContext.pageMode === "insuranceDiscovery") {
            return (
                <MUIButton
                    variant="text"
                    startIcon={<MUIArrowBackIcon />}
                    onClick={() => {
                        chartContext.resetChart();
                        if (chartContext.orderId) {
                            history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}`);
                        } else {
                            history.push(`/chart/${chartContext.patientId}`);
                        }
                        chartContext.updateInsuranceDiscoveryActiveStep(0);
                        chartContext.updateInsuranceDiscoveryApiResults(null);
                        chartContext.updateInsuranceDiscoverySelectedUser(null);
                        chartContext.updateInsuranceDiscoverySelectedPatientIndex(null);
                        chartContext.updateInsuranceDiscoveryCoverageTypes({});
                        }}
                >
                    {chartContext.orderId ? "Back to Order" : "Back to Patient"}
                </MUIButton>
            );
        }

        return (
            <>
                <MUIButton
                    variant="text"
                    onClick={() => {
                        chartContext.resetChart();

                        switch (chartContext.pageMode) {
                            case "atpAssessment":
                            case "atpAssessmentEdit":
                            case "atpAssessmentSign":
                            case "homeAssessment":
                            case "homeAssessmentEdit":
                                history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}`);
                                break;
                            default:
                                history.push(`/chart/${chartContext.patientId}`);
                                break;
                        }
                    }}
               
                >
                    Back
                </MUIButton>
           
                <MUILoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        switch (chartContext.pageMode) {
                            case "service":
                            case "billingIssue":
                            case "pickup":
                                handleClickCreateTicket();
                                break;
                            case "resupply":
                            case "new":
                                chartContext.handleSaveChart();
                                break;
                            case "atpAssessment":
                                chartContext.handleSaveChart(null, () => { history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}/atpAssessmentSign`); });
                                break;
                            case "atpAssessmentEdit":
                                chartContext.handleSaveChart(null, () => { history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}`); });
                                break;
                            case "atpAssessmentSign":
                                handleCreateAtpAssessmentSignature();
                                break;
                            case "homeAssessment":
                            case "homeAssessmentEdit":
                                chartContext.handleSaveChart(null, () => { history.push(`/chart/${chartContext.patientId}/order/${chartContext.orderId}`); });
                                break;
                        }
                    }}
                    loading={chartContext.isUpdating}
                    disabled={processingATPDocument}
                    startIcon={<MUISaveIcon />}
                >
                    {buttonText}
                </MUILoadingButton>
            </>
        )
    }

    /**
     * Renders the actions.
     * @returns {JSX.Element} Grid layout with actions display
     */
    const renderActions = () => {
        return (
            <MUIGrid item lg={"auto"} sx={{ display: "flex", justifyContent: "flex-end", marginLeft: "auto" }} key="actions">
                <MUIStack direction="row" spacing={1} alignItems="center">
                    {chartContext.pageMode ? (
                        renderNewPageModeButtons()
                    ) : (
                        <>
                            {!isPatientView && (
                                <MUIButton
                                    variant="text"
                                    onClick={() => {
                                        chartContext.handleNavigateChart(`/chart/${chartContext.patientId}`);
                                    }}
                                >
                                    Back
                                </MUIButton>
                            )}
                            <MUILoadingButton
                                variant="contained"
                                color="primary"
                                onClick={() => chartContext.handleSaveChart()}
                                loading={chartContext.isUpdating}
                                startIcon={<MUISaveButton />}
                                disabled={!chartContext.hasPendingChanges}
                            >
                                Save
                            </MUILoadingButton>
                            <Menu
                                menuItems={[
                                    // Patient menu items
                                    !orderActivity ? (
                                        <MUIMenuItem
                                            onClick={() => {
                                                chartContext.prepareOrderData("resupply");
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUIAddIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Create Re-Supply
                                        </MUIMenuItem>
                                    ) : null,
                                        !orderActivity && (
                                        patient.brightreeExternalKey ? (
                                            <MUIMenuItem
                                                onClick={() => { updateBrightreePatient(patient.id) }}
                                            >
                                                <MUIListItemIcon>
                                                    <MUICloudSyncIcon fontSize="small" />
                                                </MUIListItemIcon>
                                                Update Brightree Patient
                                            </MUIMenuItem>
                                        ) : (
                                            <MUIMenuItem
                                                onClick={() => { createBrightreePatient(patient.id) }}
                                            >
                                                <MUIListItemIcon>
                                                    <MUICloudSyncIcon fontSize="small" />
                                                </MUIListItemIcon>
                                                Create Brightree Patient
                                            </MUIMenuItem>
                                        )
                                    ),
                                    parentOrder && parentOrder.id !== chartContext.orderId && (
                                        <MUIMenuItem
                                            onClick={(e) => {
                                                window.open(`/chart/${chartContext.patientId}/order/${parentOrder.id}`, '_blank');
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUIOpenInNewIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Open Parent Order
                                        </MUIMenuItem>
                                    ),
                                    // Order menu item
                                    orderActivity && Security.hasPermission("order.syncToBrightree") && (
                                        <MUIMenuItem
                                            onClick={() => {
                                                chartContext.handleSaveChart(async () => {
                                                    await createBrightreeOrder(orderActivity.id);
                                                });
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUICloudSyncIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Create Brightree Order
                                        </MUIMenuItem>
                                    ),
                                    (
                                        <MUIMenuItem
                                            onClick={async () => {
                                                await reloadChartData();
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUICachedIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            {`Reload ${orderActivity ? `${orderActivity.orderType} ${activity?.type}` : "Patient"}`}
                                        </MUIMenuItem>
                                    ),
                                    orderActivity && (
                                        orderActivity.status === 4 ? (
                                            <MUIMenuItem
                                                onClick={() => {
                                                    setOpenDialogCancelReopenPopup(true);
                                                }}
                                            >
                                                <MUIListItemIcon>
                                                    <MUIUndoIcon fontSize="small" />
                                                </MUIListItemIcon>
                                                {`Reopen ${orderActivity.orderType} ${activity?.type}`}
                                            </MUIMenuItem>
                                        ) : null
                                    ),
                                    orderActivity && (
                                        orderActivity.status !== 4 ? (
                                            <MUIMenuItem
                                                disabled={!Security.hasPermission("order.cancel")}
                                                onClick={() => {
                                                    setOpenDialogCancelReopenPopup(true);
                                                }}
                                            >
                                                <MUIListItemIcon>
                                                    <MUIHighlightOffIcon fontSize="small" />
                                                </MUIListItemIcon>
                                                {`Cancel ${orderActivity.orderType} ${activity?.type}`}
                                            </MUIMenuItem>
                                        ) : null
                                    ),
                                    orderActivity && chartContext.chartViews && Object.values(chartContext.chartViews).length > 1 && (
                                        <MUIMenuItem
                                            onClick={() => {
                                                setSelectedChartView(chartContext.chartViews[chartContext.selectedChartViewId]);
                                                setOpenChangeViewDialog(true);
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUISpaceDashboardIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Change View
                                        </MUIMenuItem>
                                    ),
                                    orderActivity && serviceOrderReason && Security.hasPermission("order.changeParentOrder") && (
                                        <MUIMenuItem onClick={() => setOpenChangeParentOrderDialog(true)}>
                                            <MUIListItemIcon>
                                                <MUILinkIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Change Parent Order
                                        </MUIMenuItem>
                                    ),
                                    (!orderActivity || orderActivity.orderType !== "New") && (
                                        <MUIMenuItem
                                            onClick={(e) => {

                                                Sentry.captureMessage(
                                                    `Accessed old experience`,
                                                    {
                                                        fingerprint: ['oldExperience']
                                                    }
                                                );

                                                let url;
                                                if (!orderActivity) {
                                                    url = `/patientInformation/${chartContext.patientId}`;
                                                } else {
                                                    url = `/patientInformation/${chartContext.patientId}/${orderActivity.id}`;
                                                }
                                                if (e.ctrlKey || e.metaKey) {
                                                    window.open(url, '_blank');
                                                } else {
                                                    chartContext.handleNavigateChart(url);
                                                }
                                            }}
                                        >
                                            <MUIListItemIcon>
                                                <MUISwapHorizIcon fontSize="small" />
                                            </MUIListItemIcon>
                                            Old Experience
                                        </MUIMenuItem>
                                    ),
                                ].filter(Boolean)} />
                        </>
                    )}
                </MUIStack>
            </MUIGrid>
        )
    };

    /**
     * Renders the header sections with dividers between non-null sections
     * @returns {JSX.Element[]} Array of sections and dividers
     */
    const renderSectionsWithDividers = (sections) => {
        const renderedSections = sections
            .map(section => section())
            .filter(Boolean);

        const result = [];
        for (let i = 0; i < renderedSections.length; i++) {
            result.push(renderedSections[i]);
            if (i < renderedSections.length - 1) {
                result.push(
                    <MUIDivider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 2, display: { xs: 'none', sm: 'block', md: 'block', lg: 'block' } }}
                        key={`divider-${i}`}
                    />
                );
            }
        }
        return result;
    };

    const renderCreateNewOrderActivityHeader = () => {
        return (
            <MUIGrid item lg={"auto"} key="create-order-activity-header">
                <MUITypography variant="h6">{headerText}</MUITypography>
            </MUIGrid>
        )
    }

    /**
     * Renders the header.
     * @returns {JSX.Element} Grid layout with header display
     */
    return (
        <>
            <MUIGrid container alignItems="center" rowSpacing={1} sx={chartContext.pageMode ? {} : { px: 0.5, mb: 1 }}>
                {chartContext.pageMode ? (
                    chartContext.pageMode === "insuranceDiscovery" ? (
                        <>
                            {renderSectionsWithDividers([
                                renderPatientDemographics
                            ])}
                            {renderActions()}
                        </>
                    ) : (
                        <>
                            {renderSectionsWithDividers([
                                renderCreateNewOrderActivityHeader
                            ])}
                            {renderActions()}
                        </>
                    )
                ) : (
                    !orderActivity ? (
                        <>
                            {renderSectionsWithDividers([
                                renderPatientDemographics,
                                renderSalesRepAndLocation,
                                renderIndicators,
                                renderPatientBrightreeExternalId
                            ])}
                            {renderActions()}
                        </>
                    ) : (
                        <>
                            {renderSectionsWithDividers([
                                renderPatientDemographics,
                                renderOrderNumberAndServiceOrderReason,
                                renderOrderEquipmentAndSerial,
                                renderOrderInsurance,
                                renderIndicators,
                                renderOrderBrightreeExternalId,
                                renderOrderStatusReason
                            ])}
                            {renderActions()}
                            <DialogCancelReopenTicket
                                open={openDialogCancelReopenPopup}
                                onClose={() => setOpenDialogCancelReopenPopup(false)}
                            />
                        </>
                    )
                )}
            </MUIGrid>
            <MUIDialog open={openChangeViewDialog} onClose={() => setOpenChangeViewDialog(false)} maxWidth="sm" fullWidth>
                <MUIDialogTitle>Change View</MUIDialogTitle>
                <MUIDialogContent>
                    <MUIAutocomplete
                        options={Object.values(chartContext.chartViews).sort((a, b) => a.name.localeCompare(b.name))}
                        getOptionLabel={(option) => option.name}
                        value={selectedChartView}
                        onChange={(_, value) => setSelectedChartView(value)}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                label="Select View"
                                fullWidth
                                size="small"
                                variant="standard"
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                        sx={{ minWidth: 250, mt: 1 }}
                        disableClearable
                    />
                </MUIDialogContent>
                <MUIDialogActions>
                    <MUIButton onClick={() => setOpenChangeViewDialog(false)}>Cancel</MUIButton>
                    <MUIButton
                        variant="contained"
                        onClick={handleApplyChangeView}
                        disabled={!selectedChartView}
                    >
                        Apply
                    </MUIButton>
                </MUIDialogActions>
            </MUIDialog>
            {orderActivity && serviceOrderReason && (
                <DialogChangeParentOrder
                    open={openChangeParentOrderDialog}
                    onClose={() => setOpenChangeParentOrderDialog(false)}
                />
            )}
        </>
    );
}
